import { GOOGLE_MAPS_API_KEY } from "../../config";
import React, { useEffect, useRef } from "react";
import { GoogleMap, useLoadScript } from "@react-google-maps/api";
import { Button, Spin, message } from "antd";
import { CompassOutlined } from "@ant-design/icons";
import "./MapGoogle.scss";

// Librerías necesarias
const libraries = ["places", "marker"];

const extractAddressComponents = (components) => {
  const address = {
    calle: "",
    numeroExterior: "",
    colonia: "",
    codigoPostal: "",
    municipio: "",
    estado: "",
  };

  if (!components) return address;

  components.forEach((component) => {
    const types = component.types;
    const value = component.long_name || "";

    if (types.includes("route")) address.calle = value;
    if (types.includes("street_number")) address.numeroExterior = value;
    if (types.includes("sublocality") || types.includes("sublocality_level_1") || types.includes("neighborhood")) {
        address.colonia = value;
    }
    if (types.includes("postal_code")) address.codigoPostal = value;
    if (types.includes("locality") || types.includes("administrative_area_level_2")) {
       if (!address.municipio) address.municipio = value;
    }
    if (types.includes("administrative_area_level_1")) address.estado = value;
  });

  return address;
};

const MapGoogle = ({ onMapClick, onAddressSelect, markerPosition }) => {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries,
  });

  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const searchInputRef = useRef(null); 

  const MAP_ID = "c701d5cf852f82e27506e2b0"; 

  // --- 1. CONFIGURAR EL AUTOCOMPLETE ---
  useEffect(() => {
    if (isLoaded && searchInputRef.current) {
        const autocomplete = new window.google.maps.places.Autocomplete(searchInputRef.current, {
            componentRestrictions: { country: "mx" },
            fields: ["address_components", "geometry", "icon", "name", "formatted_address"],
            types: ["address"],
        });

        autocomplete.addListener("place_changed", () => {
            const place = autocomplete.getPlace();

            if (!place.geometry || !place.geometry.location) {
                message.warning("No se encontraron detalles para esta dirección");
                return;
            }

            // Mover mapa y pin
            const newPos = { 
                lat: place.geometry.location.lat(), 
                lng: place.geometry.location.lng() 
            };

            if (mapRef.current) {
                mapRef.current.panTo(newPos);
                mapRef.current.setZoom(17);
            }
            
            if (markerRef.current) {
                markerRef.current.position = newPos;
            }

            onMapClick({ latLng: { lat: () => newPos.lat, lng: () => newPos.lng } });

            if (place.address_components) {
                const addressData = extractAddressComponents(place.address_components);
                if (onAddressSelect) onAddressSelect(addressData);
            }
        });
    }
  }, [isLoaded, onMapClick, onAddressSelect]);

  // --- 2. REACCIONAR A CAMBIOS DE COORDENADAS (EDICIÓN) 🔥 AQUÍ ESTABA EL PROBLEMA ---
  useEffect(() => {
    // Si ya cargó el mapa, ya existe el marcador y nos llegan nuevas coordenadas (ej. al Editar)
    if (isLoaded && mapRef.current && markerRef.current && markerPosition) {
        const newPos = { 
            lat: parseFloat(markerPosition.lat), 
            lng: parseFloat(markerPosition.lng) 
        };
        
        // 1. Movemos el pin a la posición guardada
        markerRef.current.position = newPos;
        
        // 2. Centramos el mapa ahí
        mapRef.current.panTo(newPos);
        mapRef.current.setZoom(17);
    }
  }, [markerPosition, isLoaded]); // Se ejecuta cada vez que 'markerPosition' cambia

  // --- REVERSE GEOCODING (Pin manual) ---
  const reverseGeocode = (lat, lng) => {
    if (!window.google || !window.google.maps) return;
    const geocoder = new window.google.maps.Geocoder();

    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === "OK" && results[0]) {
        if (searchInputRef.current) {
            searchInputRef.current.value = results[0].formatted_address;
        }
        const addressData = extractAddressComponents(results[0].address_components);
        if (onAddressSelect) onAddressSelect(addressData);
      }
    });
  };

  const onMapLoad = (map) => {
    mapRef.current = map;
    if (window.google && window.google.maps && window.google.maps.marker) {
        // Creamos el marcador inicial
        const pin = new window.google.maps.marker.AdvancedMarkerElement({
            map: map,
            // Si markerPosition llega null al inicio, usa default. 
            // El useEffect de arriba lo corregirá milisegundos después si es necesario.
            position: markerPosition || { lat: 19.4326, lng: -99.1332 }, 
            title: "Ubicación",
            gmpDraggable: true,
        });

        pin.addListener("dragend", () => {
            const newPos = { lat: pin.position.lat, lng: pin.position.lng };
            onMapClick({ latLng: { lat: () => newPos.lat, lng: () => newPos.lng } });
            reverseGeocode(newPos.lat, newPos.lng);
        });
        markerRef.current = pin;
    }
  };

  const handleMapClickInternal = (e) => {
    if (!e.latLng) return;
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    if (markerRef.current) markerRef.current.position = { lat, lng };
    onMapClick(e);
    reverseGeocode(lat, lng);
  };

  const handleLocateMe = () => {
    if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
            (position) => {
                const pos = { lat: position.coords.latitude, lng: position.coords.longitude };
                if(mapRef.current) {
                    mapRef.current.panTo(pos);
                    mapRef.current.setZoom(17);
                }
                if(markerRef.current) markerRef.current.position = pos;
                onMapClick({ latLng: { lat: () => pos.lat, lng: () => pos.lng } });
                reverseGeocode(pos.lat, pos.lng);
            },
            () => message.error("No se pudo obtener la ubicación")
        );
    }
  };

  if (loadError) return <div className="map-error">Error API Key</div>;
  if (!isLoaded) return <div className="map-loading"><Spin size="large" /></div>;

  return (
    <div className="map-google-container">
      <div style={{
          position: 'absolute',
          top: '10px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 10,
          width: '90%',
          maxWidth: '400px'
      }}>
        <input
            ref={searchInputRef}
            type="text"
            placeholder="Buscar calle, colonia..."
            style={{
                width: '100%',
                height: '40px',
                padding: '0 12px',
                borderRadius: '8px',
                border: '1px solid #ccc',
                boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                fontSize: '14px',
                outline: 'none'
            }}
        />
      </div>
      
      <div className="map-frame">
          <GoogleMap
            mapContainerStyle={{ width: "100%", height: "100%" }}
            zoom={15}
            center={markerPosition || { lat: 19.4326, lng: -99.1332 }}
            onClick={handleMapClickInternal}
            onLoad={onMapLoad}
            options={{
                mapId: MAP_ID,
                disableDefaultUI: false,
                streetViewControl: false,
                mapTypeControl: false,
                fullscreenControl: false,
                clickableIcons: false,
            }}
          />
          <Button className="locate-me-btn" icon={<CompassOutlined />} onClick={handleLocateMe} />
      </div>
    </div>
  );
};

export default MapGoogle;